import { useState } from 'react'
import type { KeyboardEvent } from 'react'
import {
  Button,
  FieldError,
  Input,
  Label,
  Tag,
  TagGroup,
  TagList,
  Text,
  TextField as AriaTextField,
} from 'react-aria-components'
import { cx } from '../../cx'

export interface ChipInputProps {
  label: string
  /** The chips, in order. */
  values: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  hint?: string
  /**
   * Checks a value before it becomes a chip. Return an error message to REJECT
   * it: the text stays in the input and the message is announced.
   */
  validate?: (value: string) => string | null
  isDisabled?: boolean
  /** Keep the label for assistive tech only, when a visible heading already names the field. */
  hideLabel?: boolean
  className?: string
}

/**
 * A list of short values typed one at a time: recipients, tags. Enter, a
 * comma, or leaving the field (blur — e.g. clicking Save) turns the typed
 * text into a chip (trimmed; blanks and duplicates are ignored silently; a
 * value `validate` rejects stays in the input with its error). Each chip has
 * a remove button; Backspace on an empty input removes the last chip and
 * says so through a polite live region, since focus never sees it go.
 *
 * Built on react-aria: TextField gives the label/hint/error wiring the house
 * TextField has, TagGroup gives the chips keyboard navigation and Delete to
 * remove. Errors are text (FieldError), not colour alone.
 */
export function ChipInput({
  label,
  values,
  onChange,
  placeholder,
  hint,
  validate,
  isDisabled,
  hideLabel,
  className,
}: ChipInputProps) {
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Polite status text for removals the user did not see happen.
  const [status, setStatus] = useState('')

  const commit = () => {
    const v = draft.trim()
    if (!v) {
      setDraft('')
      return
    }
    const problem = validate?.(v) ?? null
    if (problem) {
      setError(problem)
      return
    }
    if (!values.includes(v)) onChange([...values, v])
    setDraft('')
    setError(null)
  }

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit()
    } else if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      e.preventDefault()
      const removed = values.at(-1) ?? ''
      onChange(values.slice(0, -1))
      setStatus(`Removed ${removed}`)
    }
  }

  return (
    <AriaTextField
      value={draft}
      onChange={(v) => {
        setDraft(v)
        if (error) setError(null)
        if (status) setStatus('')
      }}
      isDisabled={isDisabled}
      isInvalid={error !== null}
      className={cx('bui-field bui-chipinput', error !== null && 'bui-field-error', className)}
    >
      <Label className={hideLabel ? 'bui-sr-only' : 'bui-label'}>{label}</Label>
      <div className="bui-chipinput-box">
        {values.length > 0 ? (
          <TagGroup
            aria-label={`${label} values`}
            className="bui-chipinput-tags"
            {...(isDisabled ? {} : { onRemove: (keys: Set<unknown>) => onChange(values.filter((v) => !keys.has(v))) })}
          >
            <TagList className="bui-chipinput-list">
              {values.map((v) => (
                <Tag key={v} id={v} textValue={v} className="bui-chip bui-chip-solid bui-chipinput-chip">
                  {v}
                  {isDisabled ? null : (
                    // RAC names it "Remove" + the chip's text: "Remove a@x.io".
                    <Button slot="remove" className="bui-chipinput-x" aria-label="Remove">
                      ×
                    </Button>
                  )}
                </Tag>
              ))}
            </TagList>
          </TagGroup>
        ) : null}
        <Input className="bui-chipinput-input" placeholder={placeholder} onKeyDown={onKeyDown} onBlur={commit} />
      </div>
      <span role="status" aria-live="polite" className="bui-sr-only">
        {status}
      </span>
      {hint && error === null ? <Text slot="description" className="bui-hint">{hint}</Text> : null}
      <FieldError className="bui-hint">{error}</FieldError>
    </AriaTextField>
  )
}
